import {
	rewrite,
	RewriteOperation,
	RewriteToolContentKind,
	RewriteToolKind,
	RewriteToolTargetKind
} from "client/Api/AiAssistant/aiAssistant";
import { RewriteToolsService, RichTextEditorRewriteToolsService } from "client/Components";
import { never } from "client_legacy/ndaw/errors";
import { useMemo } from "react";

export enum RewriteToolEditorKind {
	PlainText = 1,
	RichText = 2
}

export type ResolvedService<TEditorKind extends RewriteToolEditorKind> = TEditorKind extends RewriteToolEditorKind.PlainText
	? RewriteToolsService
	: TEditorKind extends RewriteToolEditorKind.RichText
		? RichTextEditorRewriteToolsService
		: never;

export function useRewriteToolsService<TEditorKind extends RewriteToolEditorKind>(
	target: RewriteToolTargetKind,
	editor: TEditorKind
): ResolvedService<TEditorKind> {
	return useMemo(
		(): ResolvedService<TEditorKind> => {
			switch (editor) {
				case RewriteToolEditorKind.PlainText:
					return createServiceForPlainText(target) as ResolvedService<TEditorKind>;
				case RewriteToolEditorKind.RichText:
					return createServiceForRichTextEditor(target) as ResolvedService<TEditorKind>;
				default:
					never(editor);
			}
		},
		[editor, target]
	);
}

function createServiceForRichTextEditor(target: RewriteToolTargetKind): RichTextEditorRewriteToolsService {
	const scopedRewrite = async (
		content: string,
		operation: RewriteOperation,
		signal: AbortSignal | undefined
	): Promise<string> => {
		const response = await rewrite(
			{
				content,
				contentKind: RewriteToolContentKind.Html,
				operation,
				target
			},
			signal
		);

		return response.content;
	};

	return {
		makeShorter: (content, signal) => scopedRewrite(
			content,
			{ kind: RewriteToolKind.MakeShorter },
			signal
		),
		makeLonger: (content, signal) => scopedRewrite(
			content,
			{ kind: RewriteToolKind.MakeLonger },
			signal
		),
		fixSpelling: (content, signal) => scopedRewrite(
			content,
			{ kind: RewriteToolKind.FixSpelling },
			signal
		),
		changeTone: (content, tone, signal) => scopedRewrite(
			content,
			{
				kind: RewriteToolKind.ChangeTone,
				tone
			},
			signal
		),
		translate: (content, language, signal) => scopedRewrite(
			content,
			{
				kind: RewriteToolKind.Translate,
				language
			},
			signal
		)
	};
}

function createServiceForPlainText(target: RewriteToolTargetKind): RewriteToolsService {
	const scopedRewrite = async (
		content: string,
		operation: RewriteOperation,
		signal: AbortSignal | undefined
	): Promise<string> => {
		const response = await rewrite(
			{
				content,
				contentKind: RewriteToolContentKind.PlainText,
				operation,
				target
			},
			signal
		);

		return response.content;
	};

	return {
		makeShorter: (content, signal) => scopedRewrite(
			content,
			{ kind: RewriteToolKind.MakeShorter },
			signal
		),
		makeLonger: (content, signal) => scopedRewrite(
			content,
			{ kind: RewriteToolKind.MakeLonger },
			signal
		),
		fixSpelling: (content, signal) => scopedRewrite(
			content,
			{ kind: RewriteToolKind.FixSpelling },
			signal
		),
		changeTone: (content, tone, signal) => scopedRewrite(
			content,
			{
				kind: RewriteToolKind.ChangeTone,
				tone
			},
			signal
		),
		translate: (content, language, signal) => scopedRewrite(
			content,
			{
				kind: RewriteToolKind.Translate,
				language
			},
			signal
		)
	};
}
